import { useEffect, useMemo, useState } from "react"
import type { ColumnDef, PaginationState } from "@tanstack/react-table"

import { DataTable } from "@/components/DataTable"
import { KeyValue } from "@/components/KeyValue"
import { Status } from "@/components/Status"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  branchStatusLabels,
  displayBranchAddress,
  displaySubjectAddress,
  permitStatusLabels,
  subjectStatusLabels,
  type OtkBranch,
} from "@/lib/api/subjects"
import { formatUaDate } from "@/lib/utils"
import { useSession } from "@/store/session"

const keyValueRowClass = "flex flex-wrap items-baseline gap-x-2 gap-y-1"

/**
 * subject-profile.md's `/subject/profile`.
 *
 * Calls the session store's `fetchSubject()` independently of `AppShell`'s
 * header status badge (which reads the same store's `subject` field, set by
 * `RequireAuth`'s own call to the same action) so this page still gets its
 * own loading/error states — in particular the SUBJECT_NOT_CONNECTED message
 * below, which `RequireAuth`'s call swallows silently since not every route
 * needs it. Both call sites now share one `GET /subject` implementation
 * (`store/session.ts`) instead of this page calling `lib/api/subjects`
 * directly.
 *
 * The "Пункти ОТК" count and table are backed by `subject.branches`
 * (`GET /subject`'s own response — see otk-subjects.ts). subject-profile.md's
 * table also has "Дозволені категорії ТЗ"/"Стан дозволу" columns, but those
 * are per-permit fields the real `OtkBranch` type has no join for — `otk-be`
 * models the permit as one subject-wide record (`subject.permit`, shown in
 * the "Дозвільний документ" card below), not one per branch — so those two
 * columns are omitted rather than fabricated. The "Назва" cell is plain text,
 * not a `NavLink` to `/subject/punkty/:punktId` per the spec — that
 * drill-down page/route doesn't exist in this app yet.
 * `RestrictedPaymentNotice` (subject-profile.md's P-restricted use case) is
 * intentionally not wired here for the same reason: no payment/activation
 * endpoint is reachable from this subject yet (`getActivationStatus` in
 * lib/api/subjects.ts calls a route `otk-subjects.ts` doesn't define).
 */
export function SubjectProfile() {
  const subject = useSession((s) => s.subject)
  const fetchSubject = useSession((s) => s.fetchSubject)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  const columns = useMemo<ColumnDef<OtkBranch>[]>(
    () => [
      {
        accessorKey: "number",
        header: "№",
        meta: { className: "w-16" },
      },
      {
        accessorKey: "name",
        header: "Назва",
        meta: { className: "w-40" },
        cell: ({ row }) => (
          <span className="block truncate" title={row.original.name}>
            {row.original.name}
          </span>
        ),
      },
      {
        id: "address",
        header: "Адреса",
        cell: ({ row }) => displayBranchAddress(row.original),
      },
      {
        accessorKey: "punktManager",
        header: "Керівник пункту",
        meta: { className: "w-56" },
      },
      {
        accessorKey: "status",
        header: "Статус",
        meta: { className: "w-32" },
        cell: ({ row }) => (
          <Status>{branchStatusLabels[row.original.status]}</Status>
        ),
      },
    ],
    []
  )

  useEffect(() => {
    let cancelled = false
    fetchSubject()
      .catch((err) => {
        if (cancelled) return
        const code = err?.response?.data?.error as string | undefined
        setError(
          code === "SUBJECT_NOT_CONNECTED"
            ? "Обліковий запис ще не пов'язаний із суб'єктом ОТК."
            : "Не вдалося завантажити дані суб'єкта ОТК."
        )
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [fetchSubject])

  return (
    <>
      <h1 className="mb-6 text-2xl font-semibold">
        Суб'єкт ОТК — Профіль та Пункти ОТК
      </h1>

      {isLoading && (
        <p className="text-sm text-muted-foreground">Завантаження…</p>
      )}
      {!isLoading && error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      {!isLoading && subject && (
        <>
          <div className="grid items-stretch gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
            <Card className="h-full">
              <CardHeader>
                <CardTitle>Профіль Субʼєкта</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-1.5">
                <KeyValue
                  className={keyValueRowClass}
                  label="Назва:"
                  value={subject.nazva}
                />
                <KeyValue
                  className={keyValueRowClass}
                  label="ЄДРПОУ:"
                  value={subject.edrpou}
                />
                <KeyValue
                  className={keyValueRowClass}
                  label="Адреса:"
                  value={displaySubjectAddress(subject)}
                />
                <KeyValue
                  className={keyValueRowClass}
                  label="Статус:"
                  value={subjectStatusLabels[subject.status]}
                />
                <KeyValue
                  className={keyValueRowClass}
                  label="Пункти ОТК:"
                  value={String(subject.branches.length)}
                />
              </CardContent>
            </Card>
            <Card className="h-full">
              <CardHeader>
                <CardTitle>Дозвільний документ</CardTitle>
                <CardDescription>
                  Вносить ГСЦ МВС. Категорія ТЗ поза цим переліком не блокує
                  збереження чернетки, але підписання документа ОТК для неї
                  недоступне.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-1.5">
                <KeyValue
                  className={keyValueRowClass}
                  label="Номер:"
                  value={subject.permit.nomerDozvolu}
                />
                <KeyValue
                  className={keyValueRowClass}
                  label="Дата видачі:"
                  value={formatUaDate(subject.permit.dataVydachi)}
                />
                <KeyValue
                  className={keyValueRowClass}
                  label="Стан:"
                  value={<Status>{permitStatusLabels[subject.permit.status]}</Status>}
                />
                <KeyValue
                  className={keyValueRowClass}
                  label="Дозволені категорії ТЗ:"
                  value={subject.permit.kategorii.join(", ")}
                />
              </CardContent>
            </Card>
          </div>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Пункти ОТК</CardTitle>
              <CardDescription>
                Фізичні місця проведення ОТК суб'єкта. Доступ користувачів
                розмежовується по пунктах — див. розділ «Користувачі».
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={columns}
                data={subject.branches}
                getRowId={(row) => row.registryId}
                onPaginationChange={setPagination}
                pagination={pagination}
                tableClassName="table-fixed"
              />
            </CardContent>
          </Card>
        </>
      )}
    </>
  )
}
